import { Fragment } from "react";

const URL_PATTERN = /https?:\/\/[^\s<>"]+/g;

// Punctuation that ends a sentence, not the link
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

/**
 * GLE-8478
 * The short text for a link in caller notes. A Trellus link is a call
 * recording, so it reads "Recording". Any other link reads as its host.
 */
export const getNoteLinkLabel = (href: string) => {
  try {
    const host = new URL(href).hostname.replace(/^www\./, "");

    return host === "trellus.ai" || host.endsWith(".trellus.ai")
      ? "Recording"
      : host;
  } catch {
    return href;
  }
};

/**
 * Splits `text` into plain text and links. The trailing punctuation of a
 * sentence stays outside the link.
 */
export const splitNoteLinks = (text: string) => {
  const parts: Array<{ text: string; href?: string }> = [];
  let lastIndex = 0;

  // Array.from, not for...of: the build may not iterate a RegExp iterator
  Array.from(text.matchAll(URL_PATTERN)).forEach((match) => {
    const start = match.index ?? 0;
    const href = match[0].replace(TRAILING_PUNCTUATION, "");

    if (start > lastIndex) {
      parts.push({ text: text.slice(lastIndex, start) });
    }

    parts.push({ text: getNoteLinkLabel(href), href });
    lastIndex = start + href.length;
  });

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex) });
  }

  return parts;
};

/**
 * Caller notes with each link shortened to a label, so a long recording link
 * does not fill the block. The full link stays in the title and the href.
 */
export const CallerNotesText = ({ text }: { text: string }) => (
  <>
    {splitNoteLinks(text).map((part, index) =>
      part.href ? (
        <a
          key={index}
          href={part.href}
          title={part.href}
          target="_blank"
          rel="noreferrer"
          className="text-[#4474E3] underline"
        >
          {part.text}
        </a>
      ) : (
        <Fragment key={index}>{part.text}</Fragment>
      )
    )}
  </>
);
